import React from 'react';
import { Camera, RefreshCw } from 'lucide-react';

export function ScannerFlashOverlay({ show, tone = 'success' }: { show: boolean; tone?: 'success' | 'error' }) {
  if (!show) return null;
  const isError = tone === 'error';
  return (
    <div
      className="absolute inset-0 z-50 pointer-events-none"
      style={{
        border: `8px solid ${isError ? '#f87171' : '#a3e635'}`,
        backgroundColor: isError ? 'rgba(248, 113, 113, 0.15)' : 'rgba(163, 230, 53, 0.18)',
        boxShadow: isError
          ? 'inset 0 0 50px rgba(248, 113, 113, 0.5)'
          : 'inset 0 0 50px rgba(163, 230, 53, 0.65)',
        transition: 'opacity 0.15s ease-out',
      }}
    />
  );
}

export function ScannerLaserOverlay({
  active,
  instruction = 'Align barcode inside frame'
}: {
  active: boolean;
  instruction?: string;
}) {
  if (!active) return null;
  return (
    <div className="absolute inset-0 z-20 pointer-events-none flex flex-col items-center justify-center">
      <style>{`
        @keyframes laserSweepBox {
          0% { transform: translateY(0); opacity: 0.2; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(160px); opacity: 0.2; }
        }
        .animate-laser-box {
          animation: laserSweepBox 2s infinite ease-in-out;
        }
      `}</style>

      {/* Target Reticle Container */}
      <div className="relative w-[min(80vw,280px)] h-[170px] rounded-2xl flex items-center justify-center">
        {/* Darkened Vignette Mask outside reticle */}
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.42)',
          }}
        />

        {/* 4 Glowing Corner Brackets */}
        <div className="absolute top-0 left-0 w-7 h-7 border-t-[3.5px] border-l-[3.5px] border-ninpo-lime rounded-tl-xl shadow-[0_0_12px_rgba(163,230,53,0.8)]" />
        <div className="absolute top-0 right-0 w-7 h-7 border-t-[3.5px] border-r-[3.5px] border-ninpo-lime rounded-tr-xl shadow-[0_0_12px_rgba(163,230,53,0.8)]" />
        <div className="absolute bottom-0 left-0 w-7 h-7 border-b-[3.5px] border-l-[3.5px] border-ninpo-lime rounded-bl-xl shadow-[0_0_12px_rgba(163,230,53,0.8)]" />
        <div className="absolute bottom-0 right-0 w-7 h-7 border-b-[3.5px] border-r-[3.5px] border-ninpo-lime rounded-br-xl shadow-[0_0_12px_rgba(163,230,53,0.8)]" />

        {/* Center Target Aim Guide */}
        <div className="w-12 h-0.5 bg-white/20 rounded-full" />

        {/* Animated Laser sweeping strictly inside target frame */}
        <div className="absolute inset-x-2 top-2 h-0.5 bg-gradient-to-r from-transparent via-ninpo-lime to-transparent shadow-[0_0_15px_#a3e635,0_0_25px_#84cc16] animate-laser-box" />
      </div>

      {/* Guidance Pill */}
      <div className="mt-4 px-4 py-1.5 rounded-full bg-black/75 backdrop-blur-md border border-white/10 text-white/90 text-[11px] font-black uppercase tracking-widest shadow-xl flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-ninpo-lime animate-pulse shadow-[0_0_8px_#a3e635]" />
        <span>{instruction}</span>
      </div>
    </div>
  );
}

export function ScannerCameraIdleOverlay({
  scannerError,
  manualStart,
  onRetry,
}: {
  scannerError: string | null;
  manualStart: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
      <div className="text-center max-w-xs w-full flex flex-col items-center gap-3 p-6 rounded-3xl bg-white/5 border border-white/10 shadow-2xl">
        <div className="w-16 h-16 rounded-2xl bg-ninpo-lime/10 border border-ninpo-lime/20 flex items-center justify-center text-ninpo-lime">
          <Camera className="w-8 h-8" />
        </div>
        <div className="space-y-1 mt-1">
          <p className="text-sm font-black uppercase tracking-wider text-white">
            {scannerError ? 'Camera Blocked' : (manualStart ? 'Camera Ready' : 'Starting Camera…')}
          </p>
          <p className="text-xs text-slate-300 font-medium">
            {scannerError ?? (manualStart ? 'Tap below to activate your camera and scan barcodes' : 'Please grant camera access when prompted')}
          </p>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="w-full py-3.5 px-6 rounded-2xl bg-ninpo-lime hover:bg-white text-ninpo-black text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-neon transition active:scale-98 mt-2"
        >
          <RefreshCw className="w-4 h-4" /> {manualStart ? 'Start Scanner' : 'Retry Camera'}
        </button>
      </div>
    </div>
  );
}

export function ScannerErrorChip({ message }: { message: string }) {
  return (
    <div className="absolute bottom-4 left-4 right-4 z-40 text-xs text-white bg-ninpo-red/90 backdrop-blur-md border border-ninpo-red/30 rounded-2xl p-3.5 shadow-2xl text-center font-bold">
      {message}
    </div>
  );
}

export function ScannerManualUpcFallback({
  manualUpc,
  onManualUpcChange,
  onSubmit,
}: {
  manualUpc: string;
  onManualUpcChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="absolute bottom-4 left-4 right-4 z-40 rounded-2xl border border-white/20 bg-black/85 backdrop-blur-md p-4 shadow-2xl">
      <p className="text-[11px] font-black uppercase tracking-widest text-ninpo-lime">Manual UPC Entry</p>
      <p className="mt-1 text-xs text-slate-300">
        Enter barcode digits directly if scanning is unsupported.
      </p>
      <div className="mt-3 flex gap-2">
        <input
          value={manualUpc}
          onChange={(e) => onManualUpcChange(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit();
            }
          }}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          placeholder="8–14 digits"
          className="flex-1 rounded-xl border border-white/20 bg-black/60 px-3.5 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-ninpo-lime font-mono"
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={!manualUpc.trim()}
          className="rounded-xl bg-ninpo-lime px-4 py-2.5 text-xs font-black uppercase tracking-widest text-ninpo-black disabled:opacity-50 transition"
        >
          Submit
        </button>
      </div>
    </div>
  );
}
